import { useMemo, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type {
  DatesSetArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import { useAppStore } from '../../stores/appStore';

/**
 * Large, high-contrast family calendar (month / week / agenda).
 * Color-coded by calendar membership. Optimized for 40" TV viewing distance.
 */
export function FamilyCalendar() {
  const calendars = useAppStore((s) => s.calendars);
  const events = useAppStore((s) => s.events);
  const refreshEvents = useAppStore((s) => s.refreshEvents);
  const openDay = useAppStore((s) => s.openDay);
  const openEventEditor = useAppStore((s) => s.openEventEditor);
  const calRef = useRef<FullCalendar>(null);

  const colorByCal = useMemo(() => {
    const m = new Map<string, string>();
    calendars.forEach((c) => m.set(c.id, c.color));
    return m;
  }, [calendars]);

  const visibleCalIds = useMemo(
    () => new Set(calendars.filter((c) => c.visible).map((c) => c.id)),
    [calendars]
  );

  const fcEvents: EventInput[] = useMemo(() => {
    return events
      .filter((e) => visibleCalIds.has(e.calendar_id))
      .map((e) => ({
        id: e.id,
        title: e.title,
        start: e.start_at,
        end: e.end_at,
        allDay: Boolean(e.all_day),
        backgroundColor: colorByCal.get(e.calendar_id) || '#6366F1',
        borderColor: 'transparent',
        textColor: '#fff',
        extendedProps: { raw: e },
      }));
  }, [events, colorByCal, visibleCalIds]);

  const onDatesSet = useCallback(
    (arg: DatesSetArg) => {
      void refreshEvents(arg.start.toISOString(), arg.end.toISOString());
    },
    [refreshEvents]
  );

  const onDateClick = useCallback(
    (arg: DateClickArg) => {
      // FullCalendar gives dateStr as YYYY-MM-DD for all-day context
      const date = arg.dateStr.slice(0, 10);
      openDay(date);
    },
    [openDay]
  );

  const onEventClick = useCallback(
    (arg: EventClickArg) => {
      arg.jsEvent.preventDefault();
      const raw = arg.event.extendedProps.raw;
      if (raw) {
        // Open day panel for the event's day (TV-friendly) + allow edit
        const day = arg.event.startStr.slice(0, 10);
        openDay(day);
      }
    },
    [openDay]
  );

  return (
    <div className="flex h-full min-h-0 flex-col p-2 md:p-3">
      <FullCalendar
        ref={calRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,listWeek',
        }}
        buttonText={{
          today: 'Today',
          month: 'Month',
          week: 'Week',
          list: 'Agenda',
        }}
        height="100%"
        events={fcEvents}
        datesSet={onDatesSet}
        dateClick={onDateClick}
        eventClick={onEventClick}
        editable={false}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={window.innerWidth > 1400 ? 5 : 3}
        moreLinkClick="popover"
        nowIndicator={true}
        navLinks={true}
        navLinkDayClick={(date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          openDay(`${y}-${m}-${d}`);
        }}
        eventDisplay="block"
        displayEventTime={true}
        displayEventEnd={false}
        fixedWeekCount={false}
        // Large hit targets for trackpad / touch on TV
        longPressDelay={300}
      />
      {/* Mobile quick-add hint */}
      <button
        type="button"
        className="mt-2 w-full rounded-xl border border-dashed border-slate-600 py-2 text-sm text-slate-400 hover:border-violet-500 hover:text-violet-300 md:hidden"
        onClick={() => openEventEditor(null)}
      >
        + Quick add event
      </button>
    </div>
  );
}
